import {
  Box,
  Button,
  Checkbox,
  CloseButton,
  Dialog,
  Heading,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import DateRangePicker from "../../../../components/DateRangePicker";
import { toaster } from "../../../../components/ui/toaster";
import { useMerchant } from "../../../../hooks/useMerchant";
import { useTRPC } from "../../../../trpc/client";

type Props = {
  open: boolean;
  onClose: () => void;
};

const payoutStatuses = [
  { value: "pending" as const, label: "Pendiente" },
  { value: "processing" as const, label: "En Proceso" },
  { value: "completed" as const, label: "Completado" },
  { value: "cancelled" as const, label: "Cancelado" },
  { value: "declined" as const, label: "Rechazado" },
];

const DownloadReportModal = ({ open, onClose }: Props): JSX.Element => {
  const trpc = useTRPC();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState<
    ("pending" | "processing" | "completed" | "cancelled" | "declined")[]
  >(["pending", "processing", "completed", "cancelled", "declined"]);
  const [reportReference, setReportReference] = useState<string | null>(null);
  const [pollingStartedAt, setPollingStartedAt] = useState<number | null>(null);
  const { merchant } = useMerchant();

  const createPayoutReport = useMutation({
    ...trpc.payouts.createPayoutReport.mutationOptions(),
    onSuccess: (result) => {
      setReportReference(result.reference);
      setPollingStartedAt(Date.now());
      toaster.create({
        title: "Generando archivo",
        description:
          "Estamos generando tu reporte. Te avisaremos cuando termine.",
        type: "warning",
        duration: 4000,
        meta: { closable: true },
      });
      setStartDate("");
      setEndDate("");
      onClose();
    },
    onError: (error) => {
      console.error("Error creating payout report:", error);
      toaster.create({
        title: "Error",
        description: "Hubo un error al crear el reporte de pagos.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    },
  });

  const reportStatusQuery = useQuery({
    ...trpc.payouts.getPayoutReportStatus.queryOptions({
      rentalsMerchantId: merchant?.id || "",
      reference: reportReference || "",
    }),
    enabled: !!merchant?.id && !!reportReference,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === "complete") return false;
      return 2500;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (reportStatusQuery.data?.status === "complete") {
      window.location.assign(reportStatusQuery.data.downloadUrl);

      toaster.create({
        title: "Archivo listo",
        description: "El archivo se generó y se descargó automáticamente.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });

      setReportReference(null);
      setPollingStartedAt(null);
    }
  }, [reportStatusQuery.data]);

  useEffect(() => {
    if (!reportStatusQuery.isError) return;
    toaster.create({
      title: "Error",
      description:
        "Hubo un problema consultando el estado del reporte. Intenta nuevamente.",
      type: "error",
      duration: 5000,
      meta: { closable: true },
    });
    setReportReference(null);
    setPollingStartedAt(null);
  }, [reportStatusQuery.isError]);

  useEffect(() => {
    if (!reportReference || !pollingStartedAt) return;
    if (reportStatusQuery.data?.status === "complete") return;

    const timeoutId = window.setTimeout(() => {
      toaster.create({
        title: "Tiempo de espera agotado",
        description:
          "La generación del reporte está tardando más de lo esperado. Puedes intentarlo de nuevo.",
        type: "warning",
        duration: 6000,
        meta: { closable: true },
      });
      setReportReference(null);
      setPollingStartedAt(null);
    }, 10 * 60 * 1000);

    return () => window.clearTimeout(timeoutId);
  }, [reportReference, pollingStartedAt, reportStatusQuery.data?.status]);

  const handleDateRangeChange = (dates: [string, string] | undefined) => {
    setStartDate(dates?.[0] ?? "");
    setEndDate(dates?.[1] ?? "");
  };

  const handleStatusToggle = (
    statusValue: (typeof payoutStatuses)[number]["value"]
  ) => {
    const newStatuses = status.includes(statusValue)
      ? status.filter((s) => s !== statusValue)
      : [...status, statusValue];
    setStatus(newStatuses);
  };

  const handleSubmit = () => {
    createPayoutReport.mutate({
      rentalsMerchantId: merchant?.id || "",
      startDate,
      endDate,
      status,
    });
  };

  const handleClose = () => {
    setStartDate("");
    setEndDate("");
    setStatus(["pending", "processing", "completed", "cancelled", "declined"]);
    onClose();
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => !e.open && handleClose()}
      size="xl"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="md" maxWidth="32rem">
            <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
              <Dialog.Title>
                <Heading>Reporte de pagos a propietarios</Heading>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton marginTop="1" onClick={handleClose} />
            </Dialog.CloseTrigger>
            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Fechas
                  </Text>
                  <DateRangePicker
                    placeholder="Seleccionar"
                    setValues={(dates) => handleDateRangeChange(dates)}
                    values={[startDate, endDate]}
                  />
                </Box>

                <Box>
                  <Text fontWeight={600} mb={2}>
                    Estados
                  </Text>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={4}
                    bg="gray.50"
                    p={3}
                    borderRadius="md"
                  >
                    {payoutStatuses.map((s) => (
                      <Checkbox.Root
                        key={s.value}
                        checked={status.includes(s.value)}
                        onCheckedChange={() => handleStatusToggle(s.value)}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>{s.label}</Checkbox.Label>
                      </Checkbox.Root>
                    ))}
                  </Box>
                </Box>
                <Button
                  onClick={handleSubmit}
                  loading={createPayoutReport.isPending}
                  disabled={
                    startDate === "" || endDate === "" || !status.length
                  }
                >
                  Generar
                </Button>
              </VStack>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default DownloadReportModal;
